import clsx from 'clsx'
import { useRef } from 'react'
import { useToggle } from 'usehooks-ts'

import { FvButton } from '@fv/client-components'
import { useDebouncedValue } from '@fv/client-core'

import { InputAdornment, InputGroup } from '@/components/inputs/InputGroup'
import { ShipperTooltip } from '@/components/ShipperTooltip'

import { type NmfcValidationResult, useNmfcValidator } from '../../nmfc/hooks'
import { NmfcSearchPanel } from '../../nmfc/NmfcSearchPanel'
import { calculateFreightClassEstimate } from '../freight-class'
import type { DetailedItemFieldProps } from './types'

export const NmfcField = ({
  value,
  onChange,
  register,
  label = 'NMFC item #',
  ...props
}: DetailedItemFieldProps) => {
  const [isPanelOpen, togglePanel] = useToggle()
  const debouncedNmfc = useDebouncedValue(value.fullNmfc, 1500)
  const validationState = useNmfcValidator({
    code: debouncedNmfc,
    freightClass: value.freightClass,
  })
  const inputRef = useRef<HTMLInputElement>(null)
  const inputProps = register('fullNmfc')
  return (
    <>
      <InputGroup
        inputProps={{ ...inputProps, ref: inputRef }}
        helpText={
          inputProps.required
            ? 'Your account requires an NMFC item number. Enter it here to put it on the bill of lading.'
            : 'Optional: If you know your NMFC item number, enter it here to put it on the bill of lading.'
        }
        required={inputProps.required}
        inputType="text"
        label={label}
        {...props}
        endContent={
          <InputAdornment position="end">
            <ShipperTooltip label={validationState.hint}>
              <FvButton
                className={clsx({
                  '!bg-fv-beer-xlight border-2 border-fv-orange-bright outline-2 !outline-dotted !outline-fv-orange-bright !outline-offset-[3px] relative z-10':
                    validationState.state === 'error',
                  'bg-fv-green-50 border-fv-green':
                    validationState.state === 'valid',
                })}
                icon={getNmfcIcon(validationState)}
                iconClass={clsx({
                  'text-fv-green': validationState.state === 'valid',
                  'text-fv-orange-bright': validationState.state === 'error',
                })}
                transform={
                  validationState.state === 'valid' ? 'grow-5' : undefined
                }
                theme="default"
                onClick={togglePanel}
              />
            </ShipperTooltip>
          </InputAdornment>
        }
      />
      <NmfcSearchPanel
        open={isPanelOpen}
        onClose={togglePanel}
        initialSearch={value.fullNmfc}
        density={calculateFreightClassEstimate(value)?.density}
        onSelect={args => {
          inputRef.current?.setCustomValidity('')
          onChange({
            ...value,
            fullNmfc: args.nmfcCode,
            freightClass: args.freightClass,
          })
          togglePanel()
        }}
      />
    </>
  )
}

const getNmfcIcon = (result: NmfcValidationResult) => {
  switch (result.state) {
    case 'valid':
      return 'shield-check'
    case 'error':
      return 'exclamation-triangle'
    case 'unknown':
      return 'shield-check'
    case 'loading':
      return 'spinner'
  }
}
